import { Input, Select, Skeleton } from "@attmos/flowstack-ui";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { LifecycleStateLabel } from "@/api/projects";
import { useRunFeed } from "@/api/runs";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { countRuns, filterRunFeed, groupRunFeed } from "@/components/runs/grouped/group-runs";

/**
 * Behind a `React.lazy` boundary so the grouped table (+2.6 KB gzip) ships in
 * its own chunk rather than the entry chunk every route pays for, which
 * `fonts.spec.ts` budgets (flowstack#1799).
 */
const FlowRunGroupedTable = React.lazy(() =>
  import("@/components/runs/grouped/FlowRunGroupedTable").then((m) => ({
    default: m.FlowRunGroupedTable,
  }))
);

/** Every lifecycle label, in lifecycle order — the state filter must reach
 * all of them (Rupert PR-259 r1), or runs in an omitted state are unreachable. */
const STATE_OPTIONS: LifecycleStateLabel[] = [
  "validating",
  "cached",
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
  "completed",
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
];

/**
 * Tenant-wide runs feed over /api/runs, grouped by FLOW RUN (flowstack#1799):
 * one row per submission, its steps nested and collapsible, and standalone
 * runs as their own rows. Search and state filters serialize through URL
 * params (`?q=&state=`) so paste/bookmark round-trips a filtered view.
 */
export function RunListPage(): JSX.Element {
  const { data, isLoading, isError, error } = useRunFeed();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const stateFilter = searchParams.get("state") ?? "";

  const entries = React.useMemo(() => (data ? groupRunFeed(data.runs, data.flowRuns) : []), [data]);
  const visible = React.useMemo(
    () => filterRunFeed(entries, q, stateFilter),
    [entries, q, stateFilter]
  );

  const setParam = (key: string, value: string): void => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      },
      { replace: true }
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Runs" description="Tenant-wide run feed across all projects." />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-[560px] w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Runs" description="Tenant-wide run feed across all projects." />
        <EmptyState
          title="Could not load runs."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  const runs = data?.runs ?? [];
  if (runs.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Runs" description="Tenant-wide run feed across all projects." />
        <EmptyState
          title="No runs yet."
          description="Submit a Tile or a Flow from the SDK or the submission UI to populate this list."
        />
      </div>
    );
  }

  const flowRunCount = entries.filter((e) => e.kind === "flow").length;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Runs"
        description={
          runs.length === 1
            ? "1 run across all projects you can access."
            : `${runs.length} runs across all projects you can access.`
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search runs"
          placeholder="Search by id, tile, flow, step, submitter…"
          value={q}
          onChange={(e) => setParam("q", e.target.value)}
          className="max-w-sm"
        />
        <Select
          size="sm"
          aria-label="Filter by state"
          value={stateFilter}
          onChange={(e) => setParam("state", e.target.value)}
        >
          <option value="">All states</option>
          {STATE_OPTIONS.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </Select>
        <span className="ml-auto text-xs text-muted-foreground">
          {countRuns(visible)} of {runs.length} {runs.length === 1 ? "run" : "runs"}
          {flowRunCount > 0 &&
            ` · ${flowRunCount} ${flowRunCount === 1 ? "flow run" : "flow runs"}`}
        </span>
      </div>
      {visible.length === 0 ? (
        <EmptyState
          title="No runs match."
          description="Clear the search or the state filter to see every run."
        />
      ) : (
        <React.Suspense fallback={<Skeleton className="h-[560px] w-full" />}>
          <FlowRunGroupedTable
            entries={visible}
            renderRunLink={(runId, children) => (
              <Link
                to={`/runs/${encodeURIComponent(runId)}`}
                className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {children}
              </Link>
            )}
          />
        </React.Suspense>
      )}
    </div>
  );
}
